<template>
  <div class="ciw">
    <ContentSkeleton v-if="loading && !data" variant="list" :rows="4" />
    <p v-else-if="error" class="dw-empty">{{ $t("dashboardWidgets.loadError") }}</p>
    <p v-else-if="!source" class="dw-empty">{{ t("noSources") }}</p>

    <!-- Resumen: cuántas operaciones, desde cuándo, con cuántos productos y contrapartes. -->
    <template v-else-if="view === 'summary'">
      <div class="ciw-kpis">
        <div class="ciw-kpi">
          <span class="ciw-kpi__label">{{ sourceLabel(source) }}</span>
          <span class="ciw-kpi__value">{{ ops.length }}</span>
        </div>
        <div class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("first") }}</span>
          <span class="ciw-kpi__value">{{ dateText(summary.first) }}</span>
        </div>
        <div class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("last") }}</span>
          <span class="ciw-kpi__value">{{ dateText(summary.last) }}</span>
        </div>
        <div class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("productsCount") }}</span>
          <span class="ciw-kpi__value">{{ summary.products }}</span>
        </div>
        <div class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("counterpartsCount") }}</span>
          <span class="ciw-kpi__value">{{ summary.counterparts }}</span>
        </div>
        <div v-for="v in summary.totals" :key="v.currency || 'none'" class="ciw-kpi">
          <span class="ciw-kpi__label">{{ t("total") }} · {{ cur(v.currency) }}</span>
          <span class="ciw-kpi__value">{{ money(v.amount) }}</span>
        </div>
      </div>
    </template>

    <!-- Últimas operaciones, con el rol del contacto y con quién. -->
    <template v-else-if="view === 'operations'">
      <p v-if="!ops.length" class="dw-empty">{{ t("noOperations") }}</p>
      <ul v-else class="ciw-list">
        <li v-for="o in recentOps" :key="o.id" class="ciw-row">
          <span class="ciw-row__date">{{ shortDate(o.date) }}</span>
          <div class="ciw-row__body">
            <router-link :to="`/dynamic-model/${o.model}/view/${o.id}`" class="ciw-row__title">{{ o.name || "—" }}</router-link>
            <span class="ciw-row__text">
              {{ o.roles.map(roleLabel).join(", ") }}<template v-if="o.counterparts.length"> · {{ t("with", { names: o.counterparts.map(contactName).join(", ") }) }}</template>
            </span>
          </div>
          <span v-if="o.amount != null" class="ciw-row__amount">{{ money(o.amount) }} <small>{{ cur(o.currency) }}</small></span>
        </li>
      </ul>
      <p v-if="ops.length > recentOps.length" class="ciw-note">{{ t("showingLast", { n: recentOps.length, total: ops.length }) }}</p>
    </template>

    <!-- Gráficos -->
    <template v-else>
      <div v-if="caption" class="ciw-caption">{{ caption }}</div>
      <p v-if="!points.length" class="dw-empty">{{ t("noData") }}</p>
      <div v-else class="ciw-chart">
        <WidgetChart :type="chartType" :points="points" />
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Un widget de la ficha del contacto. Todos reciben la misma respuesta de
 * /api/contact-insights/:id (la carga la grilla una vez) y la agrupan según `view`.
 * Cada uno muestra una sola fuente (contratos, embarques o proformas, la que elija):
 * mezclarlas contaría dos veces la proforma y su embarque. Los montos van siempre en
 * una moneda.
 */
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import WidgetChart from "@/components/gotrading/dashboard/widgets/WidgetChart.vue";
import { CHART_TYPES } from "@/components/gotrading/dashboard/widgetData";
import insightWidgetMixin, { MONTH_OPTIONS, LIMIT_OPTIONS } from "./insightWidgetMixin";

const LIST_LIMIT = 30;

export default {
  name: "ContactInsightWidget",
  components: { ContentSkeleton, WidgetChart },
  mixins: [insightWidgetMixin],
  props: {
    entry: { type: Object, required: true },
    config: { type: Object, default: () => ({}) },
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
  },
  computed: {
    view() {
      return this.entry.view;
    },
    sources() {
      return (this.data && this.data.sources) || [];
    },
    source() {
      const keys = this.sources.map((s) => s.key);
      if (this.config.source && keys.includes(this.config.source)) return this.config.source;
      return keys[0] || null;
    },
    ops() {
      return ((this.data && this.data.operations) || []).filter((o) => o.source === this.source);
    },
    recentOps() {
      return this.ops.slice(0, LIST_LIMIT);
    },
    products() {
      return ((this.data && this.data.products) || []).filter((p) => p.source === this.source);
    },
    measure() {
      return this.config.measure === "operations" ? "operations" : "qty";
    },
    flowKind() {
      return this.config.flow === "paid" ? "paid" : "collected";
    },
    isTimeView() {
      return ["byMonth", "volume", "moneyFlow"].includes(this.view);
    },
    isMoneyView() {
      return ["volume", "productsAmount", "moneyFlow"].includes(this.view);
    },
    /** Monedas con datos para el widget, la del sistema primero. */
    currencies() {
      let list = [];
      if (this.view === "volume") list = this.ops.filter((o) => o.amount != null).map((o) => o.currency);
      else if (this.view === "productsAmount") list = this.products.filter((p) => p.amount != null).map((p) => p.currency);
      else if (this.view === "moneyFlow") list = ((this.data && this.data.flow) || []).filter((r) => r.kind === this.flowKind).map((r) => r.currency);
      const uniq = Array.from(new Set(list.map((c) => c || null)));
      const sys = this.data && this.data.systemCurrency;
      return uniq.sort((a, b) => (a === sys ? -1 : b === sys ? 1 : 0));
    },
    currency() {
      if (this.config.currency !== undefined && this.currencies.includes(this.config.currency)) return this.config.currency;
      return this.currencies.length ? this.currencies[0] : null;
    },
    summary() {
      const dates = this.ops.map((o) => o.date).filter(Boolean).sort();
      const counterparts = new Set();
      this.ops.forEach((o) => o.counterparts.forEach((c) => counterparts.add(c)));
      const totals = {};
      this.ops.forEach((o) => {
        if (o.amount == null) return;
        const k = o.currency || "none";
        totals[k] = totals[k] || { currency: o.currency || null, amount: 0 };
        totals[k].amount += o.amount;
      });
      return {
        first: dates[0] || null,
        last: dates[dates.length - 1] || null,
        products: new Set(this.products.map((p) => p.product)).size,
        counterparts: counterparts.size,
        totals: Object.values(totals),
      };
    },
    points() {
      if (this.view === "byMonth") return this.perMonth(this.ops.map((o) => ({ month: (o.date || "").slice(0, 7), value: 1 })));
      if (this.view === "volume") {
        return this.perMonth(this.ops.filter((o) => o.amount != null && (o.currency || null) === this.currency).map((o) => ({ month: (o.date || "").slice(0, 7), value: o.amount })));
      }
      if (this.view === "moneyFlow") {
        const rows = ((this.data && this.data.flow) || []).filter((r) => r.kind === this.flowKind && (r.currency || null) === this.currency);
        return this.perMonth(rows.map((r) => ({ month: r.month, value: r.amount })));
      }
      if (this.view === "byRole") {
        const map = {};
        this.ops.forEach((o) => o.roles.forEach((r) => (map[r] = (map[r] || 0) + 1)));
        return this.top(Object.entries(map).map(([r, value]) => ({ label: this.roleLabel(r), value })));
      }
      if (this.view === "counterparts") {
        const map = {};
        this.ops.forEach((o) => o.counterparts.forEach((c) => (map[c] = (map[c] || 0) + 1)));
        return this.top(Object.entries(map).map(([c, value]) => ({ label: this.contactName(c), value })));
      }
      if (this.view === "products") {
        // Por cantidad cada unidad va aparte (kg y toneladas no se suman).
        const map = {};
        this.products.forEach((p) => {
          const byQty = this.measure === "qty";
          const key = byQty ? `${p.product}|${p.unit || ""}` : p.product;
          const label = byQty && p.unitLabel && !this.singleUnit ? `${p.unitLabel} · ${p.label}` : p.label;
          map[key] = map[key] || { label, value: 0 };
          map[key].value += byQty ? p.qty : p.operations;
        });
        return this.top(Object.values(map));
      }
      if (this.view === "productsAmount") {
        const map = {};
        this.products
          .filter((p) => p.amount != null && (p.currency || null) === this.currency)
          .forEach((p) => {
            map[p.product] = map[p.product] || { label: p.label, value: 0 };
            map[p.product].value += p.amount;
          });
        return this.top(Object.values(map));
      }
      return [];
    },
    /** Si todos los productos van en la misma unidad, va una vez en el título y no en cada barra. */
    singleUnit() {
      const units = new Set(this.products.map((p) => p.unitLabel || ""));
      return units.size === 1 ? Array.from(units)[0] : "";
    },
    caption() {
      const parts = [];
      if (this.sources.length > 1) parts.push(this.sourceLabel(this.source));
      if (this.isMoneyView) parts.push(this.cur(this.currency));
      if (this.view === "moneyFlow") parts.push(this.t(`flow.${this.flowKind}`));
      if (this.view === "products") parts.push(this.t(`measure.${this.measure}`) + (this.measure === "qty" && this.singleUnit ? ` (${this.singleUnit})` : ""));
      return parts.join(" · ");
    },
    menuItems() {
      const items = [];
      if (this.entry.chart) {
        items.push({ divider: true, key: "d-chart", label: this.$tn("dashboardWidgets.settings.chartType") });
        CHART_TYPES.forEach((k) => items.push({ key: `chart:${k}`, label: this.$tn(`dashboardWidgets.settings.type.${k}`), active: k === this.chartType }));
      }
      if (this.sources.length > 1) {
        items.push({ divider: true, key: "d-source", label: this.tn("menu.source") });
        this.sources.forEach((s) => items.push({ key: `source:${s.key}`, label: this.tn(`sources.${s.key}`), active: s.key === this.source }));
      }
      if (this.isTimeView) {
        items.push({ divider: true, key: "d-months", label: this.tn("menu.period") });
        MONTH_OPTIONS.forEach((n) => items.push({ key: `months:${n}`, label: n ? this.tn("menu.months", { n }) : this.tn("menu.allTime"), active: n === this.months }));
      }
      if (["byRole", "counterparts", "products", "productsAmount"].includes(this.view)) {
        items.push({ divider: true, key: "d-limit", label: this.tn("menu.show") });
        LIMIT_OPTIONS.forEach((n) => items.push({ key: `limit:${n}`, label: this.tn("menu.top", { n }), active: n === this.limit }));
      }
      if (this.view === "products") {
        items.push({ divider: true, key: "d-measure", label: this.tn("menu.measure") });
        ["qty", "operations"].forEach((k) => items.push({ key: `measure:${k}`, label: this.tn(`measure.${k}`), active: k === this.measure }));
      }
      if (this.view === "moneyFlow") {
        items.push({ divider: true, key: "d-flow", label: this.tn("menu.flow") });
        ["collected", "paid"].forEach((k) => items.push({ key: `flow:${k}`, label: this.tn(`flow.${k}`), active: k === this.flowKind }));
      }
      if (this.isMoneyView && this.currencies.length > 1) {
        items.push({ divider: true, key: "d-cur", label: this.$tn("dashboardWidgets.finance.currency") });
        this.currencies.forEach((c) => items.push({ key: `currency:${c || ""}`, label: this.cur(c), active: c === this.currency }));
      }
      return items;
    },
  },
  watch: {
    menuItems: {
      immediate: true,
      handler(items) {
        this.$emit("menu", items);
      },
    },
  },
  methods: {
    t(key, params) {
      return this.$t(`contactWidgets.${key}`, params);
    },
    tn(key, params) {
      return this.$tn(`contactWidgets.${key}`, params);
    },
    onMenu(key) {
      const [name, ...rest] = key.split(":");
      const value = rest.join(":");
      if (name === "chart") this.$emit("update-config", { chart: value });
      else if (name === "source") this.$emit("update-config", { source: value });
      else if (name === "months") this.$emit("update-config", { months: parseInt(value, 10) });
      else if (name === "limit") this.$emit("update-config", { limit: parseInt(value, 10) });
      else if (name === "measure") this.$emit("update-config", { measure: value });
      else if (name === "flow") this.$emit("update-config", { flow: value });
      else if (name === "currency") this.$emit("update-config", { currency: value || null });
    },
    sourceLabel(key) {
      return key ? this.t(`sources.${key}`) : "";
    },
    roleLabel(role) {
      return this.t(`roles.${role}`);
    },
  },
};
</script>

<style scoped>
.ciw { display: flex; flex-direction: column; gap: 8px; height: 100%; min-height: 0; }
.ciw-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 8px; }
.ciw-kpi { display: flex; flex-direction: column; gap: 2px; padding: 8px 10px; border-radius: 8px; background: var(--dw-surface-2); min-width: 0; }
.ciw-kpi__label { font-size: 0.72rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ciw-kpi__value { font-size: 1.15rem; font-weight: 700; color: var(--dw-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ciw-caption { font-size: 0.75rem; color: var(--dw-muted); }
.ciw-chart { flex: 1; min-height: 120px; }
.ciw-list { list-style: none; margin: 0; padding: 0; overflow-y: auto; min-height: 0; }
.ciw-row { display: flex; align-items: center; gap: 10px; padding: 7px 0; border-bottom: 1px solid var(--dw-border); }
.ciw-row:last-child { border-bottom: 0; }
.ciw-row__date { font-size: 0.75rem; font-weight: 600; min-width: 58px; color: var(--dw-muted); }
.ciw-row__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.ciw-row__title { font-weight: 600; font-size: 0.84rem; color: var(--dw-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
a.ciw-row__title:hover { color: var(--bs-primary); }
.ciw-row__text { font-size: 0.74rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ciw-row__amount { font-size: 0.82rem; font-weight: 600; white-space: nowrap; }
.ciw-row__amount small { font-size: 0.66rem; color: var(--dw-muted); }
.ciw-note { margin: 0; font-size: 0.7rem; color: var(--dw-muted); }
</style>
